import React from 'react';
import { Box, Typography, Container, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../layout';

const RiskDisclaimer = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Logo />
          <Button onClick={() => navigate(-1)} sx={{ ml: 'auto' }}>
            Back
          </Button>
        </Box>

        <Typography variant="h4" component="h1" gutterBottom>
          Risk Disclaimer
        </Typography>

        <Typography variant="body1" paragraph>
          Last updated: February 19, 2026
        </Typography>

        <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
          PLEASE READ THIS RISK DISCLAIMER CAREFULLY BEFORE USING NANOTRADINGCOPILOT.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          1. Not Financial Advice
        </Typography>
        <Typography variant="body1" paragraph>
          NanoTradingCopilot is a pre-trade validation tool. It validates your trade ideas against rules that you define. It does NOT provide financial advice, investment recommendations, trading signals, or predictions of market movements.
        </Typography>
        <Typography variant="body1" paragraph>
          The Service is designed to help you follow your own trading rules — not to tell you what to trade, when to trade, or how to trade.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          2. Trading Risk
        </Typography>
        <Typography variant="body1" paragraph>
          Trading in financial markets involves substantial risk of loss and is not suitable for every person. You should carefully consider whether trading is appropriate for you in light of your financial condition, investment experience, and risk tolerance.
        </Typography>
        <Typography variant="body1" paragraph>
          You could lose some or all of your invested capital. Do not trade with money you cannot afford to lose.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          3. Past Performance
        </Typography>
        <Typography variant="body1" paragraph>
          Past performance is not indicative of future results. Historical data, market analysis, or validation results shown through the Service are for informational purposes only and should not be construed as a guarantee of future performance.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          4. No Guarantee of Accuracy
        </Typography>
        <Typography variant="body1" paragraph>
          While we strive to provide accurate validation and market data, we make no guarantees about the accuracy, completeness, reliability, or timeliness of any information provided through the Service. Market data is sourced from third-party providers and may be delayed, incomplete, or contain errors.
        </Typography>
        <Typography variant="body1" paragraph>
          Validation results are based on rules you configure and market data available at the time of validation. Conditions can change rapidly. A trade that passes validation may still result in a loss.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          5. Your Responsibility
        </Typography>
        <Typography variant="body1" paragraph>
          All trading decisions are solely your responsibility. The Service is a tool to assist you in following your rules — it does not replace your judgment, due diligence, or the advice of a qualified financial professional.
        </Typography>
        <Typography variant="body1" paragraph>
          You are solely responsible for:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Defining appropriate trading rules for your strategy and risk tolerance</Typography>
          <Typography component="li">Verifying that validation results are consistent with current market conditions</Typography>
          <Typography component="li">Making the final decision to enter or not enter any trade</Typography>
          <Typography component="li">Managing your positions and risk after entering a trade</Typography>
          <Typography component="li">Complying with all applicable laws and regulations in your jurisdiction</Typography>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          6. Not a Regulated Entity
        </Typography>
        <Typography variant="body1" paragraph>
          NanoTradingCopilot is not a registered investment advisor, broker-dealer, financial analyst, or financial planner. We are not licensed, registered, or regulated by any financial regulatory authority in any jurisdiction.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          7. Leverage and Margin Trading
        </Typography>
        <Typography variant="body1" paragraph>
          If you trade using leverage (including forex, CFDs, or futures), you should be aware that leverage amplifies both potential profits and potential losses. You can lose more than your initial deposit. NanoTradingCopilot's position sizing calculations are based on the parameters you provide and do not account for margin requirements, broker-specific rules, or slippage.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          8. Service Availability
        </Typography>
        <Typography variant="body1" paragraph>
          The Service may be unavailable or experience delays due to maintenance, technical issues, or circumstances beyond our control. You should not rely solely on the Service for time-critical trading decisions. Always have a backup plan for accessing market data and executing trades.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          9. Limitation of Liability
        </Typography>
        <Typography variant="body1" paragraph>
          NanoTradingCopilot, its officers, directors, employees, and affiliates shall not be liable for any trading losses, financial damages, or other consequences arising from your use of the Service. This includes, without limitation, losses resulting from:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li">Reliance on validation results or market data provided through the Service</Typography>
          <Typography component="li">Technical errors, data inaccuracies, or service interruptions</Typography>
          <Typography component="li">Trades executed or not executed based on the Service's output</Typography>
          <Typography component="li">Misconfigured rules or account settings</Typography>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          10. Seek Professional Advice
        </Typography>
        <Typography variant="body1" paragraph>
          Before making any trading or investment decisions, you should consult with a qualified financial advisor, licensed broker, or other professional who can assess your individual financial situation, objectives, and risk tolerance.
        </Typography>

        <Typography variant="body1" paragraph sx={{ mt: 3, fontWeight: 'bold' }}>
          By using NanoTradingCopilot, you acknowledge that you have read and understood this Risk Disclaimer, and you accept full responsibility for your trading decisions.
        </Typography>
      </Box>
    </Container>
  );
};

export default RiskDisclaimer;
